import React from "react";
import { motion } from "framer-motion";
import Heading from "../../atoms/Heading/Heading";

import whySattvaTechGradient from "../../../app/assets/images/homepage/outcomebg.webp";
import plusPattern from "../../../app/assets/images/homepage/boxcard.webp";

interface WhySattvaTechHighlight {
  title: string;
  blurb: string;
}

interface WhySattvaTechSectionData {
  label?: string;
  headline?: string;
  subhead?: string;
  highlights?: WhySattvaTechHighlight[];
}

/* CARD DATA */
const highlightsData: WhySattvaTechHighlight[] = [
  {
    title: "Founder-led, engineer-run",
    blurb:
      "You speak directly with the people building your product. No account managers buffering technical decisions or translating requirements.",
  },
  {
    title: "20-person team, zero outsourcing",
    blurb:
      "Every line of code written in-house. Your IP stays clean, your codebase stays coherent, and your team actually understands what was built.",
  },
  {
    title: "ROI first scoping",
    blurb:
      "We define the metric we're moving before we start. Automation that cannot be measured does not get built and billed.",
  },
  {
    title: "Agentic-native stack",
    blurb:
      "We work with the tools running in production in 2026: LLM APIs, n8n, Supabase, FastAPI not legacy RPA platforms with new branding.",
  },
];

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.12,
      duration: 0.65,
      ease: "easeOut",
    },
  }),
};

const WhySattvaTechSection = ({
  data,
}: {
  data?: WhySattvaTechSectionData;
}) => {
  return (
    <section className="relative overflow-hidden bg-[#0F0F0F] py-16 md:py-24">
      {/* BACKGROUND IMAGE */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <img
          src={whySattvaTechGradient}
          alt=""
          className="
            absolute
            left-1/2
            top-0
            h-full
            w-full
            max-w-[1400px]
            -translate-x-1/2
            object-cover
            opacity-60
          "
        />

      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        className="relative z-10 mx-auto max-w-[1240px] px-5"
      >
        {/* HEADING */}
        <div className="flex justify-center text-center">
          <Heading
            title={data?.label || "WHY THE SATTVA TECH"}
            subTitle={data?.headline || "Four things that separate serious engineering from vendor work."}
            className="!px-0"
          />
        </div>


        {/* CARDS */}
        <div className="mt-5 md:mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
          {highlightsData.map((item, index) => (
            <motion.div
              key={item.title}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="
                group
                relative
                overflow-hidden
                rounded-[15px]
                border
                border-[#229CFF]
                bg-[#171717]
                p-4 md:p-7
                transition-all
                duration-300
                hover:-translate-y-1
              "
            >
              {/* BLUE BLUR GLOW */}
              <div
                className="
                  pointer-events-none
                  absolute
                  left-[-60px]
                  top-1/2
                  z-0
                  h-[240px]
                  w-[65%]
                  -translate-y-1/2
                  rounded-full
                  bg-[#229CFF]/20
                  blur-[95px]
                "
              />

              {/* LEFT TO RIGHT GRADIENT */}
              <div
                className="
                  pointer-events-none
                  absolute
                  left-0
                  top-0
                  h-full
                  w-[75%]
                  bg-gradient-to-r
                  from-[#229CFF]/16
                  via-[#229CFF]/7
                  to-transparent
                "
              />

              {/* CORNER PATTERN */}
              <img
                src={plusPattern}
                alt=""
                className="
                  absolute
                  right-0
                  top-0
                  w-[48px]
                  opacity-100
                "
              />

              {/* CONTENT */}
              <div className="relative z-10">
                <h3
                  className="
                    max-w-[90%]
                    text-[20px]
                    font-google-sans-bold
                    leading-[1.2]
                    text-white
                    md:text-[25px]
                  "
                >
                  {item.title}
                </h3>

                <p
                  className="
                    md:mt-5 mt-2
                    max-w-[95%] text-sm
                    md:text-[16px]
                    md:leading-[1.8]
                    text-white/65
                  "
                >
                  {item.blurb}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default WhySattvaTechSection;